import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { FormPartComponent } from '@/app/shared/_components/form-part-component';
import { ApiService } from '@/app/shared/_services/api.service';
import { DspBuyersQueryParams, TtdBuyerType, TtdSeatOption } from '@/app/shared/_models/dsp-buyer.model';

@Component({
  selector: 'app-ttd-buyer-dropdown',
  templateUrl: './ttd-buyer-dropdown.component.html',
  styleUrls: ['./ttd-buyer-dropdown.component.less'],
  providers: FormPartComponent.createDirectiveProviders(TtdBuyerDropdownComponent),
})
export class TtdBuyerDropdownComponent
  extends FormPartComponent<string, FormControl<string>>
  implements OnInit {
  buyers: TtdSeatOption[] = [];
  customBuyer: TtdSeatOption | null = null;
  isLoading = false;

  private _buyerType: TtdBuyerType = TtdBuyerType.Agency;
  private initialized = false;

  @Input() showSyncHint = true;
  @Input() selectId: string | null = null;

  @Input()
  get buyerType(): TtdBuyerType {
    return this._buyerType;
  }
  set buyerType(value: TtdBuyerType) {
    if (this._buyerType === value) return;
    this._buyerType = value;
    if (this.initialized) {
      this.onBuyerTypeChange();
    }
  }

  private readonly _formPart = new FormControl<string>(null);
  private endCursor: string | null = null;
  private hasNextPage = false;
  private searchText = '';
  private searchSubject$ = new Subject<string>();
  private lastSearchText = '';
  private cancelFetch$ = new Subject<void>();
  private selectedBuyerCache: TtdSeatOption | null = null;

  private readonly PAGE_SIZE = 50;
  private readonly SEARCH_DEBOUNCE_MS = 500;

  constructor(private apiService: ApiService, private destroyRef: DestroyRef) {
    super();
  }

  protected override get formPart(): FormControl<string> {
    return this._formPart;
  }

  override writeValue(value: string | string[]): void {
    const normalized = Array.isArray(value) ? value[0] || null : value;
    super.writeValue(normalized);
    if (normalized) {
      this.resolveSelectedBuyer(normalized);
    }
  }

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.trackSelectedBuyer();
    this.fetchBuyers();
    this.initialized = true;
  }

  get isCustomBuyerSelected(): boolean {
    return !!this.selectedBuyerCache?.custom;
  }

  buyerLabel(buyer: TtdSeatOption): string {
    return buyer.custom ? buyer.id : `${buyer.name} (${buyer.id})`;
  }

  get isSyncHintVisible(): boolean {
    return this.showSyncHint && this.isCustomBuyerSelected;
  }

  onSearch(searchText: string): void {
    this.searchSubject$.next(searchText);
  }

  onScrollToBottom(): void {
    if (this.hasNextPage && !this.isLoading) {
      this.fetchBuyers(true);
    }
  }

  private onBuyerTypeChange(): void {
    this._formPart.setValue(null);
    this.searchText = '';
    this.lastSearchText = '';
    this.fetchBuyers();
  }

  protected override getFormPartValidationErrors(): ValidationErrors {
    return null;
  }

  private setupSearchDebounce(): void {
    this.searchSubject$.pipe(
      debounceTime(this.SEARCH_DEBOUNCE_MS),
      filter(search => search !== this.lastSearchText),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(search => {
      this.lastSearchText = search;
      this.searchText = search;
      this.fetchBuyers();
    });
  }

  private trackSelectedBuyer(): void {
    this._formPart.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => this.cacheSelectedBuyer(value));
  }

  private cacheSelectedBuyer(buyerId: string | null): void {
    const candidates = this.customBuyer ? [...this.buyers, this.customBuyer] : this.buyers;
    this.selectedBuyerCache = candidates.find(b => b.id === buyerId) ?? null;
    if (this.selectedBuyerCache === this.customBuyer) {
      this.customBuyer = null;
    }
    this.prependCachedBuyer();
  }

  private resolveSelectedBuyer(buyerId: string): void {
    this.apiService.getDspBuyers('TTD', { buyerId, limit: 1 }).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => {
        if (this._formPart.value !== buyerId) {
          return;
        }
        this.selectedBuyerCache = res.data.items[0] ?? this.toCustomBuyer(buyerId);
        this.prependCachedBuyer();
      },
      error: () => {
        if (this._formPart.value === buyerId) {
          this.selectedBuyerCache = this.toCustomBuyer(buyerId);
          this.prependCachedBuyer();
        }
      },
    });
  }

  private fetchBuyers(loadMore = false): void {
    this.cancelFetch$.next();

    if (!loadMore) {
      this.resetPagination();
    }

    this.isLoading = true;

    const params = this.buildQueryParams(loadMore);

    this.apiService.getDspBuyers('TTD', params).pipe(
      takeUntil(this.cancelFetch$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => {
        const newBuyers = res.data.items;
        this.buyers = loadMore ? [...this.buyers, ...newBuyers] : newBuyers;

        if (!loadMore) {
          this.prependCachedBuyer();
        }

        this.endCursor = res.data.meta.endCursor;
        this.hasNextPage = res.data.meta.hasNextPage;
        this.isLoading = false;
        this.updateCustomBuyer();
      },
      error: () => {
        this.isLoading = false;
        this.updateCustomBuyer();
      },
    });
  }

  private resetPagination(): void {
    this.buyers = [];
    this.customBuyer = null;
    this.endCursor = null;
    this.hasNextPage = false;
  }

  private buildQueryParams(loadMore: boolean): DspBuyersQueryParams {
    return {
      limit: this.PAGE_SIZE,
      buyerType: this.buyerType,
      ...(this.searchText ? { buyerId: this.searchText } : {}),
      ...(loadMore && this.endCursor ? { after: this.endCursor } : {}),
    };
  }

  private updateCustomBuyer(): void {
    const seatId = this.searchText.trim();
    const hasExactMatch = this.buyers.some(b => b.id === seatId);
    this.customBuyer = seatId && !hasExactMatch ? this.toCustomBuyer(seatId) : null;
  }

  private toCustomBuyer(seatId: string): TtdSeatOption {
    return { id: seatId, name: seatId, custom: true };
  }

  private prependCachedBuyer(): void {
    if (this.selectedBuyerCache && !this.buyers.some(b => b.id === this.selectedBuyerCache.id)) {
      this.buyers = [this.selectedBuyerCache, ...this.buyers];
    }
  }
}
